import { Link } from 'react-router';
import { formatDateTime } from '../../lib/format';
import { t } from '../../lib/i18n';
import { Card, EmptyState, Spinner, TableWrap, Th } from '../../components/ui';
import { ErrorPanel } from '../../components/ErrorPanel';
import { useRetention } from './api';

/** Where the setting lives in the portal: platform settings, or the organisation settings page (site and group rules are edited on their own pages from there). */
const settingsLink = (scope: string): string => (scope === 'platform' ? '/platform/settings' : '/admin/settings');

/** How long each kind of data is kept, with a link to its setting, and what the clean-up jobs deleted lately. */
export function RetentionTab() {
  const q = useRetention();
  if (q.isError) return <ErrorPanel error={q.error} onRetry={() => void q.refetch()} />;
  if (!q.data) return <Spinner />;
  const { rules, recentRuns } = q.data;
  return (
    <div className="space-y-4">
      <Card title={t('p4dp.retention.title')} description={t('p4dp.retention.help')}>
        {rules.length === 0 ? (
          <EmptyState message={t('p4dp.retention.noRules')} />
        ) : (
          <TableWrap caption={t('p4dp.retention.title')}>
            <thead className="border-b border-slate-200 bg-slate-50">
              <tr>
                <Th>{t('p4dp.retention.col.what')}</Th>
                <Th>{t('p4dp.retention.col.module')}</Th>
                <Th>{t('p4dp.retention.col.period')}</Th>
                <Th>{t('p4dp.retention.col.where')}</Th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {rules.map((r) => {
                const what = t(r.labelKey);
                return (
                  <tr key={`${r.module}:${r.labelKey}:${r.settingPath}`}>
                    <td className="px-4 py-2.5 font-medium">{what}</td>
                    <td className="px-4 py-2.5">{r.module}</td>
                    <td className="px-4 py-2.5">{r.days === null ? t('p4dp.retention.varies') : r.days === 0 ? t('p4dp.retention.forever') : t('p4dp.retention.days', { days: r.days })}</td>
                    <td className="px-4 py-2.5">
                      {t(`p4dp.retention.scope.${r.scope}`)}{' '}
                      <Link className="font-medium text-brand underline" to={settingsLink(r.scope)} aria-label={t('p4dp.retention.changeFor', { what })}>
                        {t('p4dp.retention.change')}
                      </Link>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </TableWrap>
        )}
      </Card>
      <Card title={t('p4dp.retention.runs')}>
        {recentRuns.length === 0 ? (
          <EmptyState message={t('p4dp.retention.noRuns')} />
        ) : (
          <TableWrap caption={t('p4dp.retention.runs')}>
            <thead className="border-b border-slate-200 bg-slate-50">
              <tr>
                <Th>{t('p4dp.retention.run.module')}</Th>
                <Th>{t('p4dp.retention.run.action')}</Th>
                <Th>{t('p4dp.retention.run.when')}</Th>
                <Th>{t('p4dp.retention.run.deleted')}</Th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {recentRuns.map((r, i) => (
                <tr key={`${r.module}:${r.at}:${i}`}>
                  <td className="px-4 py-2.5">{r.module}</td>
                  <td className="px-4 py-2.5">{r.action}</td>
                  <td className="px-4 py-2.5">{formatDateTime(r.at)}</td>
                  <td className="px-4 py-2.5">{r.deleted}</td>
                </tr>
              ))}
            </tbody>
          </TableWrap>
        )}
      </Card>
    </div>
  );
}
